import React, { useState, useEffect, useMemo, useRef } from "react";
import { Select, message, Spin, Tooltip, Radio, Space, Row, Col, Checkbox } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import aircraftApi from "../../api/v3/aircraftApi";
import { getColorByString } from "../../utils/colorUtils";
import { AirplaneIcon } from "../icons/SvgIcons";

// Module-level cache
let cachedAircrafts = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

const fetchAircraftData = async () => {
    const now = Date.now();
    if (cachedAircrafts && cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
        return cachedAircrafts;
    }

    try {
        const response = await aircraftApi.getAircraftList({ page_size: 500 });
        const aircraftList = response?.data?.items || [];
        if (aircraftList.length > 0) {
            cachedAircrafts = aircraftList;
            cacheTimestamp = now;
            return aircraftList;
        }
        return [];
    } catch (error) {
        console.error("获取飞机列表失败:", error);
        message.error("获取飞机列表失败");
        // Return old cache if available, otherwise empty array
        return cachedAircrafts || [];
    }
};

/**
 * 可复用的飞机选择器组件
 * @param {object} props - 组件属性，透传给 antd Select 组件 (如 value, onChange, placeholder, disabled, style, etc.)
 * @param {string | number} props.value - 当前选中的飞机ID或注册号 (取决于 valueType)
 * @param {function} props.onChange - 选中项变化时的回调函数
 * @param {'id' | 'registration'} [props.valueType='registration'] - `value` 和 `onChange` 回调参数是飞机ID还是注册号
 * @param {'simple' | 'detailed' | 'full'} [props.displayMode='simple'] - 预设的显示模式
 *        - simple: 只显示 AircraftTag
 *        - detailed: 显示 AircraftTag + 类型
 *        - full: 显示 AircraftTag + 所有额外字段
 * @param {string[]} [props.extraFields] - 需要在标签中额外显示的飞机字段数组，可选值:
 *        - 'aircraft_type': 飞机类型 (如 波音737-800)
 *        - 'flight_number_offset': 航班号偏移量
 *        - 'seat_count': 座位数
 *        - 'id': 飞机ID
 *        - 'owner': 所有者
 *        - 'status': 飞机状态
 * @param {function} [props.renderLabel] - 自定义渲染标签的函数 (aircraft) => ReactNode
 * @param {boolean} [props.tiled=false] - 是否使用平铺模式 (Radio.Group) 而不是下拉选择 (Select)
 * @param {number} [props.tiledColumns=4] - 平铺模式下每行显示的列数
 * @param {string} [props.tiledSize='middle'] - 平铺模式下 Radio.Button 的大小，可选 'small', 'middle', 'large'
 * @param {Array} [props.aircrafts] - 外部传入的飞机数据；未传入时组件自行加载
 * @param {boolean} [props.defaultSelectFirst=false] - 单选模式下列表加载完成且当前无选中项时，自动选中第一架飞机
 */
const AircraftSelector = ({ valueType = "registration", displayMode = "simple", extraFields = [], renderLabel, onChange, tiled = false, tiledColumns = 4, tiledSize = "middle", aircrafts, defaultSelectFirst = false, value, mode, ...restProps }) => {
    const [aircraftOptions, setAircraftOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const defaultSelectedValueRef = useRef(null);

    useEffect(() => {
        if (Array.isArray(aircrafts)) {
            setAircraftOptions(aircrafts);
            setLoading(false);
            return undefined;
        }

        let isMounted = true;
        const loadAircraft = async () => {
            setLoading(true);
            const data = await fetchAircraftData();
            if (isMounted) {
                setAircraftOptions(data);
                setLoading(false);
            }
        };
        loadAircraft();
        return () => {
            isMounted = false;
        };
    }, [aircrafts]);

    // 处理预设模式的字段显示
    const getFieldsToDisplay = () => {
        if (displayMode === "full") {
            return ["aircraft_type", "flight_number_offset", "seat_count", "owner", "status"];
        } else if (displayMode === "detailed") {
            return ["aircraft_type", "flight_number_offset"];
        }

        // 如果用户明确指定了extraFields，则使用用户指定的
        return extraFields;
    };

    // 渲染额外字段
    const renderExtraField = (aircraft, field) => {
        const formatters = {
            aircraft_type: (v) => `${v || "-"}`,
            flight_number_offset: (v) => (
                <span>
                    <Tooltip title="航班号偏移量：用于自动生成航班号时的数字偏移。例如，偏移2, 则航班号为航段的基础号+2。">
                        {v || "0"} <InfoCircleOutlined style={{ fontSize: 12, color: "#999", marginLeft: 2 }} />
                    </Tooltip>
                </span>
            ),
            seat_count: (v) => `座位数: ${v || "未知"}`,
            id: (v) => `ID: ${v || "未知"}`,
            owner: (v) => `所有者: ${v || "未知"}`,
            status: (v) => `状态: ${v || "未知"}`,
        };

        const value = aircraft[field];
        const formatter = formatters[field] || ((v) => `${field}: ${v || "未知"}`);

        return formatter(value);
    };

    // 默认标签渲染函数 - 带外框的现代设计
    const defaultRenderLabel = (aircraft) => {
        const fieldsToDisplay = getFieldsToDisplay();
        const registration = aircraft.registration;
        const color = !registration || registration === "未分配" || registration === "未知" ? "#999" : getColorByString(registration);

        const showIcon = registration && registration !== "未分配" && registration !== "未知";

        return (
            <div
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    maxWidth: "100%",
                    border: `1px solid ${color}`,
                    borderRadius: "4px",
                    backgroundColor: "#fafafa",
                    padding: "0 6px",
                    height: "22px",
                    boxSizing: "border-box",
                }}
            >
                {/* 图标与注册号主体 */}
                <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                    {showIcon && (
                        <span style={{ marginRight: 4, display: "flex", alignItems: "center" }}>
                            <AirplaneIcon color={color} style={{ marginRight: 0, top: 0, width: "14px", height: "14px" }} />
                        </span>
                    )}
                    <span
                        style={{
                            fontWeight: 600,
                            color: "#1f2937",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                            fontSize: "13px",
                            lineHeight: "20px",
                        }}
                    >
                        {registration || "未分配"}
                    </span>
                </div>

                {/* 额外信息字段 */}
                {fieldsToDisplay.length > 0 && (
                    <div style={{ display: "flex", alignItems: "center", fontSize: 12, color: "#6b7280", marginLeft: 6, paddingLeft: 6, borderLeft: "1px solid #e5e7eb", height: "12px", lineHeight: "12px" }}>
                        {/* Map fields */}
                        {fieldsToDisplay.map((field, index) => (
                            <React.Fragment key={field}>
                                {index > 0 && <span style={{ margin: "0 3px" }}>•</span>}
                                {field === "flight_number_offset" ? <span style={{ display: "flex", alignItems: "center" }}>{renderExtraField(aircraft, field)}</span> : <span>{renderExtraField(aircraft, field)}</span>}
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // 为平铺模式准备的紧凑渲染函数
    const compactRenderLabel = (aircraft) => {
        const fieldsToDisplay = getFieldsToDisplay();
        const registration = aircraft.registration;
        const color = getColorByString(registration || "");

        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", marginBottom: 2 }}>
                    <AirplaneIcon color={color} style={{ width: 14, height: 14, marginRight: 4, top: 0 }} />
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{registration}</span>
                </div>
                {fieldsToDisplay.includes("aircraft_type") && <span style={{ fontSize: 11, color: "#666", lineHeight: 1 }}>{aircraft.aircraft_type || "-"}</span>}
            </div>
        );
    };

    const options = useMemo(() => {
        return aircraftOptions.map((aircraft) => ({
            value: valueType === "id" ? aircraft.id : aircraft.registration,
            label: renderLabel ? renderLabel(aircraft) : defaultRenderLabel(aircraft),
            compactLabel: compactRenderLabel(aircraft), // 为平铺模式添加紧凑标签
            // 保存原始数据用于筛选
            aircraft: aircraft,
            // For filtering
            searchValue: `${aircraft.registration} ${aircraft.aircraft_type || ""} ${aircraft.owner || ""}`.toLowerCase(),
        }));
    }, [aircraftOptions, valueType, displayMode, extraFields, renderLabel]);

    useEffect(() => {
        if (!defaultSelectFirst || mode === "multiple" || mode === "tags" || !onChange) return;

        const hasSelectedValue = value !== undefined && value !== null && value !== "";
        if (hasSelectedValue) {
            defaultSelectedValueRef.current = null;
            return;
        }

        const firstOption = options[0];
        if (!firstOption || firstOption.value === undefined || firstOption.value === null || firstOption.value === "") return;
        if (defaultSelectedValueRef.current === firstOption.value) return;

        defaultSelectedValueRef.current = firstOption.value;
        onChange(firstOption.value, firstOption);
    }, [defaultSelectFirst, mode, onChange, options, value]);

    const filterOption = (input, option) => {
        return option?.searchValue?.includes(input.toLowerCase());
    };

    // 处理选择变更，确保传递option参数
    const handleChange = (value, option) => {
        if (onChange) {
            onChange(value, option);
        }
    };

    // 平铺模式下的变更处理
    const handleTiledChange = (e) => {
        const value = e.target.value;
        // 找到对应的option
        const option = options.find((opt) => opt.value === value);
        if (onChange) {
            onChange(value, option);
        }
    };

    // 多选平铺模式下的变更处理
    const handleTiledMultipleChange = (checkedValue) => {
        if (onChange) {
            // 获取所有选中选项的详细信息
            const selectedOptions = options.filter((opt) => checkedValue.includes(opt.value));
            onChange(checkedValue, selectedOptions);
        }
    };

    // 如果是平铺模式
    if (tiled) {
        // 多选模式
        if (mode === "multiple") {
            return (
                <div style={{ width: "100%" }}>
                    <Spin spinning={loading}>
                        <Checkbox.Group value={value} onChange={handleTiledMultipleChange} style={{ width: "100%" }}>
                            <Row gutter={[8, 8]}>
                                {options.map((option) => (
                                    <Col span={6} key={option.value}>
                                        <Checkbox
                                            value={option.value}
                                            style={{
                                                width: "100%",
                                                minWidth: 0,
                                                marginRight: 0,
                                            }}
                                        >
                                            <div
                                                style={{
                                                    padding: "2px",
                                                    // border: "1px solid #d9d9d9",
                                                    // borderRadius: "2px",
                                                    textAlign: "center",
                                                    width: "100%",
                                                    minWidth: 0,
                                                    overflow: "hidden",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                {option.compactLabel}
                                            </div>
                                        </Checkbox>
                                    </Col>
                                ))}
                            </Row>
                        </Checkbox.Group>
                    </Spin>
                </div>
            );
        }

        // 单选模式
        return (
            <div style={{ width: "100%" }}>
                <Spin spinning={loading}>
                    <Radio.Group size={tiledSize} value={value} onChange={handleTiledChange} style={{ width: "100%" }} optionType="button" buttonStyle="solid">
                        <Row gutter={[8, 8]}>
                            {options.map((option) => (
                                <Col span={4} key={option.value}>
                                    <Radio.Button
                                        value={option.value}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            textAlign: "center",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {option.compactLabel}
                                    </Radio.Button>
                                </Col>
                            ))}
                        </Row>
                    </Radio.Group>
                </Spin>
            </div>
        );
    }

    // 默认返回Select组件
    return (
        <Select
            showSearch
            allowClear
            placeholder="选择或搜索飞机"
            loading={loading}
            options={options}
            value={value}
            mode={mode}
            optionFilterProp="searchValue" // Use custom searchValue for filtering
            filterOption={filterOption}
            onChange={handleChange}
            {...restProps}
            style={{ ...restProps.style, margin: 0 }}
        />
    );
};

export default AircraftSelector;
